import React, { useState, createContext, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import DeviceSelection from './components/DeviceSelection';
import DeviceModelList from './components/DeviceModelList';
import DeviceSpecifications from './components/DeviceSpecifications';
import CartPage from './components/CartPage';
import Header, { LanguageContext, NavigationContext } from './components/Header';
import Footer from './components/Footer';
import HomeLayout from './components/HomeLayout';
import SuccessPage from './components/SuccessPage';
import CancelReservationPage from './components/CancelReservationPage';
import { loadTranslations, getTranslation, preloadTranslations, SUPPORTED_LANGUAGES } from './translations';
import type { Language } from './translations';

// Global CSS reset with Repair module design system
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    background: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    background: #ffffff;
    transition: background 0.3s ease;
    color: #0f172a;
    line-height: 1.55;
  }

  /* Minimal resets — no global overrides to avoid conflicts with component styles */
  button { cursor: pointer; }
  input, textarea, select, button { font-family: inherit; }

  /* Dark mode */
  .dark body,
  .dark html {
    background: #0a0c10;
    color: #f1f5f9;
  }

  .dark h1, .dark h2, .dark h3 {
    color: #f1f5f9;
  }

  .dark h4, .dark h5, .dark h6 {
    color: #cbd5e1;
  }

  .dark p {
    color: #9ca3af;
  }

  /* Responsive Typography */
  @media (max-width: 640px) {
    h1 {
      font-size: 1.75rem;
    }
    h2 {
      font-size: 1.3rem;
    }
    h3 {
      font-size: 1rem;
    }
    p {
      font-size: 0.8125rem;
    }
  }
`;

const MainContent = styled.main`
  flex: 1 0 auto;
  width: 100%;

  /* Remove all padding that creates gaps */
  padding: 0;
  margin: 0;

  /* Ensure seamless connection with header and footer */
  position: relative;
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  position: relative;
  transition: background 0.3s ease;
  margin: 0;
  padding: 0;
`;

const HeaderWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isTransitioning',
})<{ isTransitioning: boolean }>`
  opacity: ${props => props.isTransitioning ? 0 : 1};
  transition: opacity 250ms cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => props.isTransitioning ? 'none' : 'auto'};
`;

const FooterWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isTransitioning',
})<{ isTransitioning: boolean }>`
  opacity: ${props => props.isTransitioning ? 0 : 1};
  transition: opacity 250ms cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => props.isTransitioning ? 'none' : 'auto'};
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

interface PageTransitionProps {
  children: React.ReactNode;
  isVisible: boolean;
  animationDuration?: number;
}

const PageTransition = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isVisible' && prop !== 'animationDuration',
})<PageTransitionProps>`
  animation: ${props => props.isVisible ? fadeIn : fadeOut} ${props => props.animationDuration || 300}ms cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.isVisible ? 1 : 0};
  position: relative;
  width: 100%;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
`;

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
  font-family: open-sans, sans-serif;
  font-weight: 600;
  color: #212529;
  margin: 0px 0px 5px;
  padding: 0px 7.5px;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 16px;
  font-family: open-sans, sans-serif;
  font-weight: 500;
  color: #212529;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 3rem;
`;

const StepTitle = styled.p`
  text-align: center;
  color: #058DD5;
  font-size: 30px;
  font-family: open-sans, sans-serif;
  font-weight: 600;
  margin-bottom: 0px;
`;

const StepSubtitle = styled.h2`
  text-align: center;
  font-size: 24px;
  font-family: open-sans, sans-serif;
  color: #212529;
  margin: 0px 0px 5px;
`;

type Page = 'deviceTypeSelection' | 'modelSelection' | 'specifications' | 'cart' | 'success';

// Dynamic spec item for displaying user's answers in cart/checkout
// Stores both Dutch (for backend/calendar) and current language (for frontend display)
export interface DynamicSpecItem {
  questionKey: string;
  optionKey: string;
  // Dutch labels - used for backend logs and calendar (always in Dutch)
  question_nl: string;
  option_nl: string;
  // Current language labels - used for frontend display (dynamic based on user's language)
  question_label: string;
  option_label: string;
  // Full multilingual JSONB - used to re-resolve labels when language changes
  question_labels?: Record<string, string>;
  option_labels?: Record<string, string>;
}

export interface CartItem {
  id: string;
  deviceType: string;
  modelName: string;
  /** Customer-facing name from device_models.display_name; modelName stays the key. */
  displayName?: string;
  image: string;
  specs: {
    storage: string;
    battery?: string;
    networkType?: string;
    condition?: string;
    // iPhone specific properties
    functionality?: string;
    screenCondition?: string;
    housingCondition?: string;
    batteryCapacity?: string;
    imageSrc?: string;
    // iMac specific properties
    screenSize?: string;
    modelYear?: string;
    processor?: string;
    ram?: string;
    accessories?: string;
    // Apple Watch specific properties
    watchSize?: string;
    watchBand?: string;
    cellularSupport?: string;
    // Dynamic specs array containing all answered questions with their labels
    dynamicSpecs?: DynamicSpecItem[];
  };
  price: number;
  quantity: number;
}

// Cart Context voor globale toegang tot winkelwagen
export const CartContext = createContext<{
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  quantities: Record<string, number>;
  updateQuantity: (id: string, quantity: number) => void;
}>({
  cartItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  clearCart: () => {},
  quantities: {},
  updateQuantity: () => {},
});

export const ModalContext = createContext<{
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
}>({
  isModalOpen: false,
  setIsModalOpen: () => {},
});

// Cancel link from the confirmation email:
// ?cancel_order=<order_number>&cancel_token=<token>&lang=<lang>
// Older emails have no cancel_token; the cancel page then shows a contact message.
interface CancelLink {
  orderNumber: string;
  token: string | null;
  lang: Language | null;
}

const readCancelLinkFromUrl = (): CancelLink | null => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('cancel_order')) {
    return null;
  }

  const lang = params.get('lang');
  return {
    orderNumber: (params.get('cancel_order') || '').trim(),
    token: params.get('cancel_token')?.trim() || null,
    lang: lang && (SUPPORTED_LANGUAGES as string[]).includes(lang) ? (lang as Language) : null,
  };
};

function App() {
  // Read once on first render; a cancel link replaces the normal booking flow
  const [cancelLink, setCancelLink] = useState<CancelLink | null>(readCancelLinkFromUrl);
  const [currentPage, setCurrentPage] = useState<Page>('deviceTypeSelection');
  const [selectedDeviceType, setSelectedDeviceType] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [selectedModelName, setSelectedModelName] = useState<string | null>(null);
  const [macGroupFilter, setMacGroupFilter] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [language, setLanguage] = useState<Language>(() => cancelLink?.lang ?? 'nl');
  const [isTranslationLoading, setIsTranslationLoading] = useState(true);
  // Keep <html lang> in sync with the chosen language (correct uppercase for tr, e.g. "i" -> "İ", and for screen readers)
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successData, setSuccessData] = useState({
    orderNumber: '',
    email: '',
    appointmentDetails: null as any
  });

  // Translation helper function
  const t = useCallback((key: string): string => {
    return getTranslation(language, key);
  }, [language]);

  // Load translations on mount and language change
  useEffect(() => {
    const loadLanguageTranslations = async () => {
      setIsTranslationLoading(true);
      try {
        await loadTranslations(language);
        // Also preload Dutch as fallback
        if (language !== 'nl') {
          await loadTranslations('nl');
        }
      } catch (error) {
        console.error('Failed to load translations:', error);
      } finally {
        setIsTranslationLoading(false);
      }
    };
    loadLanguageTranslations();
  }, [language]);

  // Handle language change with preloading
  const handleSetLanguage = useCallback(async (newLang: string) => {
    const lang = newLang as Language;
    if (lang !== language) {
      await loadTranslations(lang);
      setLanguage(lang);
    }
  }, [language]);
  
  // State for page transition animation
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [visiblePage, setVisiblePage] = useState<Page>('deviceTypeSelection');
  
  const topAnchorRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);



  // Handle page transitions
  useEffect(() => {
    if (currentPage !== visiblePage) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setVisiblePage(currentPage);
        setIsTransitioning(false);
      }, 300); // Match this with animation duration
      return () => clearTimeout(timer);
    }
  }, [currentPage, visiblePage]);

  useEffect(() => {
    const smoothScrollToTop = () => {
      // Use only smooth scrolling methods
      if (topAnchorRef.current) {
        topAnchorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        // Fallback smooth scroll
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      
      // Additional smooth scroll after a short delay to ensure we reach the top
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 150);
    };

    // Use a slight delay to ensure DOM is fully rendered
    const timer = setTimeout(smoothScrollToTop, 200);
    return () => clearTimeout(timer);
  }, [currentPage]);


  // Function to safely change pages with animation
  const changePage = (newPage: Page) => {
    if (currentPage !== newPage) {
      setCurrentPage(newPage);
    }
  };

  const handleDeviceTypeSelect = (deviceType: string) => {
    setSelectedDeviceType(deviceType);
    changePage('modelSelection');
  };

  const handleModelSelect = (modelId: string, modelName?: string) => {
    setSelectedModel(modelId);
    setSelectedModelName(modelName || null);
    changePage('specifications');
  };

  const handleBack = () => {
    // Eerst naar boven scrollen

    // Daarna pagina veranderen
    if (currentPage === 'modelSelection') {
      if (macGroupFilter) {
        // Mac model page → terug naar Mac categorie selectie
        setMacGroupFilter(null);
      } else {
        setSelectedDeviceType(null);
        changePage('deviceTypeSelection');
      }
    } else if (currentPage === 'specifications') {
      setSelectedModel(null);
      setSelectedModelName(null);
      changePage('modelSelection');
    } else if (currentPage === 'cart') {
      setSelectedDeviceType(null);
      setSelectedModel(null);
      setSelectedModelName(null);
      setMacGroupFilter(null);
      changePage('deviceTypeSelection');
    }
  };

  // Leave the cancel page: drop the cancel params from the URL and show the normal start page
  const handleCancelLinkDone = () => {
    window.history.replaceState(null, '', '/');
    setCancelLink(null);
  };

  const goToHome = () => {
    // Eerst naar boven scrollen

    // Daarna pagina veranderen
    // The logo also works on the cancel page, so leave that page first
    if (cancelLink) {
      handleCancelLinkDone();
    }
    setSelectedDeviceType(null);
    setSelectedModel(null);
    setSelectedModelName(null);
    setMacGroupFilter(null);
    changePage('deviceTypeSelection');
  };

  const goToCart = () => {
    // Eerst naar boven scrollen

    // Daarna pagina veranderen
    // The cart icon also works on the cancel page, so leave that page first
    if (cancelLink) {
      handleCancelLinkDone();
    }
    changePage('cart');
  };

  const goToSuccessPage = (orderNumber: string, email: string = '', appointmentDetails?: any) => {
    setSuccessData({
      orderNumber,
      email,
      appointmentDetails
    });
    // Change page BEFORE clearing cart to avoid race condition where
    // the cart-empty useEffect redirects to homepage before success page renders
    changePage('success');
    clearCart();
  };

  const handleSellClick = (specs: {
    storage: string;
    networkType?: string;
    battery?: string;
    condition?: string;
    // iPhone specific properties
    functionality?: string;
    screenCondition?: string;
    housingCondition?: string;
    batteryCapacity?: string;
    price: number;
    imageSrc?: string;
    displayName?: string;
    // iMac specific properties
    screenSize?: string;
    modelYear?: string;
    processor?: string;
    ram?: string;
    accessories?: string;
    // Apple Watch specific properties
    watchSize?: string;
    watchBand?: string;
    cellularSupport?: string;
  }) => {
    if (selectedDeviceType && selectedModel) {
      // Use image from specs (comes from database via DeviceSpecifications)
      // Fallback to default if not provided
      const imagePath = specs.imageSrc || '/devices/default.png';

      const newItem: CartItem = {
        id: Date.now().toString(),
        deviceType: selectedDeviceType,
        modelName: selectedModel,
        displayName: specs.displayName || selectedModelName || undefined,
        image: imagePath,
        specs,
        price: specs.price,
        quantity: 1
      };

      console.log(`[Winkelmandje] Item toegevoegd met afbeelding: ${imagePath}`);
      addToCart(newItem);
      setCurrentPage('cart');
    }
  };

  const handleRemoveFromCart = (id: string) => {
    setCartItems(cartItems.filter(item => item.id !== id));
    // Verwijder ook de hoeveelheid voor dit item
    const newQuantities = { ...quantities };
    delete newQuantities[id];
    setQuantities(newQuantities);
  };

  const addToCart = (item: CartItem) => {
    setCartItems([...cartItems, item]);
    // Stel de standaard hoeveelheid in
    setQuantities(prev => ({ ...prev, [item.id]: 1 }));
  };

  const updateQuantity = (id: string, quantity: number) => {
    setQuantities(prev => ({ ...prev, [id]: quantity }));
  };

  const clearCart = () => {
    setCartItems([]);
    setQuantities({});
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, isLoading: isTranslationLoading, t }}>
      <CartContext.Provider value={{ cartItems, addToCart, removeFromCart: handleRemoveFromCart, clearCart, quantities, updateQuantity }}>
        <ModalContext.Provider value={{ isModalOpen, setIsModalOpen }}>
          <NavigationContext.Provider value={{ goToHome, goToCart }}>
            <GlobalStyle />
            <AppContainer>
              {/* Top anchor div for scrolling */}
              <div 
                ref={topAnchorRef} 
                tabIndex={-1} 
                style={{ position: 'absolute', top: 0, height: 0 }} 
                aria-hidden="true"
              />
              <HeaderWrapper isTransitioning={isTransitioning}>
                <Header />
              </HeaderWrapper>
              <MainContent ref={mainContentRef}>
                {cancelLink ? (
                  <CancelReservationPage
                    orderNumber={cancelLink.orderNumber}
                    token={cancelLink.token}
                    onDone={handleCancelLinkDone}
                  />
                ) : (
                  <>
                    <PageTransition isVisible={visiblePage === 'deviceTypeSelection' && !isTransitioning} animationDuration={300}>
                      {(visiblePage === 'deviceTypeSelection' || (currentPage === 'deviceTypeSelection' && isTransitioning)) && (
                      <HomeLayout>
                        <DeviceSelection onDeviceTypeSelect={handleDeviceTypeSelect} />
                      </HomeLayout>
                    )}
                    </PageTransition>
                    
                    <PageTransition isVisible={visiblePage === 'modelSelection' && !isTransitioning} animationDuration={300}>
                      {(visiblePage === 'modelSelection' || (currentPage === 'modelSelection' && isTransitioning)) && selectedDeviceType && (
                      <DeviceModelList
                        deviceType={selectedDeviceType as any}
                        onSelect={handleModelSelect}
                        onBack={handleBack}
                        macGroupFilter={macGroupFilter}
                        onMacGroupSelect={setMacGroupFilter}
                      />
                    )}
                    </PageTransition>
                    
                    <PageTransition isVisible={visiblePage === 'specifications' && !isTransitioning} animationDuration={300}>
                      {(visiblePage === 'specifications' || (currentPage === 'specifications' && isTransitioning)) && selectedDeviceType && selectedModel && (
                      <DeviceSpecifications
                        deviceType={selectedDeviceType}
                        modelName={selectedModel}
                        modelKey={selectedModel}
                        modelDisplayName={selectedModelName ?? undefined}
                        onSellClick={handleSellClick}
                      />
                    )}
                    </PageTransition>
                    
                    <PageTransition isVisible={visiblePage === 'cart' && !isTransitioning} animationDuration={300}>
                      {(visiblePage === 'cart' || (currentPage === 'cart' && isTransitioning)) && (
                      <CartPage 
                        items={cartItems}
                        onRemoveItem={handleRemoveFromCart}
                        onBack={handleBack}
                        onSuccessNavigate={goToSuccessPage}
                      />
                    )}
                    </PageTransition>
                    
                    <PageTransition isVisible={visiblePage === 'success' && !isTransitioning} animationDuration={300}>
                      {(visiblePage === 'success' || (currentPage === 'success' && isTransitioning)) && (
                      <SuccessPage 
                        orderNumber={successData.orderNumber}
                        email={successData.email}
                        onBackToHome={goToHome}
                        deliveryMethod={successData.appointmentDetails?.method as 'store' | 'home'}
                        customerName={successData.appointmentDetails?.customerName || { firstName: '', lastName: '' }}
                        appointment={successData.appointmentDetails || {}}
                      />
                    )}
                    </PageTransition>
                  </>
                )}
              </MainContent>
              <FooterWrapper isTransitioning={isTransitioning}>
                <Footer />
              </FooterWrapper>
            </AppContainer>
          </NavigationContext.Provider>
        </ModalContext.Provider>
      </CartContext.Provider>
    </LanguageContext.Provider>
  );
}

export default App;